import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { getExercise, equipmentLabel, exercisesBase } from '../../lib/exercises';
import { getEquipmentItem, equipmentBase } from '../../lib/equipment';
import { SITE_URL, getStrings, htmlLang, type Lang } from '../../lib/i18n';
import { workoutStrings, fmt, toolPath, type WorkoutStrings } from '../../lib/tools';
import {
  ARMS,
  DAY_OPTIONS,
  DEFAULT_INPUTS,
  EQUIPMENT_OPTIONS,
  FOCUSES,
  LEVELS,
  MINUTE_OPTIONS,
  generatePlan,
  parseInputs,
  recoveryItems,
  serializeInputs,
  type EquipmentOption,
  type Inputs,
  type Plan,
  type PlanItem,
  type Range,
  type Session,
  type Swaps,
} from '../../lib/workoutGenerator';
import { CopyButton } from './ToolParts';

export const WORKOUT_SLUG = 'armwrestling-workout-generator';

export const EXAMPLE_INPUTS: Inputs = {
  level: 'intermediate',
  days: 3,
  minutes: 60,
  equipment: ['dumbbell', 'band', 'cable', 'table'],
  focus: 'toproll',
  arm: 'right',
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const range = ([a, b]: Range) => (a === b ? `${a}` : `${a}–${b}`);

function restText(r: Range, t: WorkoutStrings): string {
  if (r[0] >= 120) return fmt(t.plan.minutesUnit, { n: range([r[0] / 60, r[1] / 60]) });
  return fmt(t.plan.seconds, { n: range(r) });
}

function weekdayName(weekday: number, lang: Lang): string {
  // 2024-01-01 was a Monday.
  const d = new Date(Date.UTC(2024, 0, 1 + weekday));
  const s = new Intl.DateTimeFormat(htmlLang[lang], { weekday: 'long', timeZone: 'UTC' }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function equipmentOptionLabel(o: EquipmentOption, lang: Lang, t: WorkoutStrings): string {
  if (o === 'loadingPin') return t.form.loadingPin;
  if (o === 'barbell') return `${equipmentLabel('barbell', lang)} / ${equipmentLabel('ezBar', lang)}`;
  return equipmentLabel(o, lang);
}

interface ResolvedItem {
  name: string;
  href: string;
  dose: string;
}

function resolveItem(item: PlanItem, lang: Lang, t: WorkoutStrings): ResolvedItem | null {
  if (item.ref.kind === 'exercise') {
    const ex = getExercise(lang, item.ref.slug);
    if (!ex) return null;
    let dose: string;
    if (item.useLibraryDose) dose = `${ex.t.sets} · ${ex.t.reps}`;
    else if (item.holdSec) dose = fmt(t.plan.setsHold, { sets: item.sets, hold: fmt(t.plan.seconds, { n: range(item.holdSec) }) });
    else dose = fmt(t.plan.setsReps, { sets: item.sets, reps: range(item.reps || [10, 12]) });
    if (item.restSec) dose += ` · ${fmt(t.plan.rest, { rest: restText(item.restSec, t) })}`;
    return { name: ex.t.name, href: `${exercisesBase(lang)}/${ex.slug}`, dose };
  }
  const eq = getEquipmentItem(lang, item.ref.slug);
  if (!eq) return null;
  const href = `${equipmentBase(lang)}/${eq.slug}`;
  if (item.role === 'table') {
    return { name: t.plan.tableName, href, dose: '' };
  }
  // Wrist roller accessory
  let dose = fmt(t.plan.wristRollerDose, { sets: item.sets });
  if (item.restSec) dose += ` · ${fmt(t.plan.rest, { rest: restText(item.restSec, t) })}`;
  return { name: t.plan.wristRollerName, href, dose };
}

// ---------------------------------------------------------------------------
// Plan view (also used for the static example plan)
// ---------------------------------------------------------------------------

function ItemRow({
  item,
  lang,
  t,
  onShuffle,
  patternLabel,
}: {
  item: PlanItem;
  lang: Lang;
  t: WorkoutStrings;
  onShuffle?: (key: string) => void;
  patternLabel: string;
}) {
  const r = resolveItem(item, lang, t);
  if (!r) return null;
  const canShuffle = !!onShuffle && !!item.slotKey && item.options > 1;
  return (
    <li className="flex items-start justify-between gap-3 py-2.5 border-b border-line last:border-b-0 break-inside-avoid">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <a href={r.href} className="font-bold text-ink hover:text-[var(--brand-ink)] underline-offset-2 hover:underline">
            {r.name}
          </a>
          {item.role === 'primary' && (
            <span className="rounded-full bg-[var(--brand-soft)] text-[var(--brand-ink)] text-[10px] font-black uppercase tracking-wide px-2 py-0.5">
              {t.plan.focusTag}
            </span>
          )}
          {item.role === 'light' && (
            <span className="rounded-full bg-elevated text-muted text-[10px] font-black uppercase tracking-wide px-2 py-0.5">
              {t.plan.lightTag}
            </span>
          )}
        </div>
        <p className="text-sm text-muted mt-0.5">
          {item.role !== 'warmup' && <span className="text-faint">{patternLabel} · </span>}
          {r.dose}
        </p>
      </div>
      {onShuffle && item.slotKey && (
        <button
          type="button"
          onClick={() => canShuffle && onShuffle(item.slotKey!)}
          disabled={!canShuffle}
          title={canShuffle ? undefined : t.actions.noAlternative}
          aria-label={fmt(t.actions.shuffleAria, { name: r.name, pattern: patternLabel })}
          className="btn-3d btn-3d--ghost btn-3d--sm flex-shrink-0 px-3 py-1.5 text-xs print:hidden"
        >
          <span aria-hidden="true">⇄</span> {t.actions.shuffle}
        </button>
      )}
    </li>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-4">
      <h4 className="text-xs font-black uppercase tracking-wider text-faint mb-1">{title}</h4>
      <ul>{children}</ul>
    </div>
  );
}

function SessionCard({
  session,
  weekday,
  level,
  lang,
  t,
  onShuffle,
}: {
  session: Session;
  level: Inputs['level'];
  weekday: number;
  lang: Lang;
  t: WorkoutStrings;
  onShuffle?: (key: string) => void;
}) {
  const pl = t.plan.patterns as Record<string, string>;
  const subtitle = session.light
    ? t.plan.lightSession
    : session.focusPatterns.map((p) => pl[p]).join(' + ');
  return (
    <article className="card-chunky p-4 sm:p-5 break-inside-avoid">
      <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-lg font-black text-ink">
          {weekdayName(weekday, lang)} · {fmt(t.plan.session, { n: session.index + 1 })}
        </h3>
        <p className="text-sm font-bold text-[var(--brand-ink)]">{subtitle}</p>
      </header>

      <Block title={t.plan.warmup}>
        {session.warmup.map((w) => (
          <ItemRow key={w.ref.id} item={w} lang={lang} t={t} patternLabel={pl.warmup} />
        ))}
        <li className="py-2 text-sm text-faint">{t.plan.warmupRamp}</li>
      </Block>

      {session.table && <TableBlock item={session.table} level={level} lang={lang} t={t} />}

      {session.main.length > 0 && (
        <Block title={t.plan.main}>
          {session.main.map((m) => (
            <ItemRow key={m.slotKey} item={m} lang={lang} t={t} onShuffle={onShuffle} patternLabel={pl[m.pattern]} />
          ))}
        </Block>
      )}

      {session.accessory.length > 0 && (
        <Block title={t.plan.accessory}>
          {session.accessory.map((a) => (
            <ItemRow key={a.slotKey} item={a} lang={lang} t={t} onShuffle={onShuffle} patternLabel={pl[a.pattern]} />
          ))}
        </Block>
      )}
    </article>
  );
}

function TableBlock({ item, level, lang, t }: { item: PlanItem; level: Inputs['level']; lang: Lang; t: WorkoutStrings }) {
  const r = resolveItem(item, lang, t);
  if (!r || !item.durationMin) return null;
  const min = range(item.durationMin);
  return (
    <Block title={t.plan.table}>
      <li className="py-2.5">
        <a href={r.href} className="font-bold text-ink hover:text-[var(--brand-ink)] hover:underline">{r.name}</a>
        <p className="text-sm text-muted mt-0.5">{fmt(t.plan.tableNotes[level], { min })}</p>
      </li>
    </Block>
  );
}

function RecoveryCard({ weekday, lang, t }: { weekday: number; lang: Lang; t: WorkoutStrings }) {
  const items = recoveryItems(lang)
    .map((ref) => getExercise(lang, ref.slug))
    .filter((e): e is NonNullable<typeof e> => !!e);
  return (
    <article className="rounded-3xl border-2 border-dashed border-line p-4 sm:p-5 break-inside-avoid">
      <h3 className="text-base font-black text-ink">
        {weekdayName(weekday, lang)} · <span className="text-faint">{t.plan.recoveryTitle}</span>
      </h3>
      <p className="text-sm text-muted mt-1">
        {t.plan.recoveryBody}{' '}
        {items.map((e, i) => (
          <span key={e.id}>
            {i > 0 && ', '}
            <a href={`${exercisesBase(lang)}/${e.slug}`} className="font-semibold text-[var(--brand-ink)] hover:underline">
              {e.t.name}
            </a>
          </span>
        ))}
        .
      </p>
    </article>
  );
}

export function PlanView({
  plan,
  lang,
  onShuffle,
  headingLevel = 'h2',
  showNotes = true,
}: {
  plan: Plan;
  lang: Lang;
  onShuffle?: (key: string) => void;
  headingLevel?: 'h2' | 'h3';
  showNotes?: boolean;
}) {
  const t = workoutStrings(lang);
  const c = getStrings(lang).exercisesChrome;
  const pl = t.plan.patterns as Record<string, string>;
  const { inputs } = plan;
  const summary = fmt(t.plan.summary, {
    level: c.difficulty[inputs.level],
    days: inputs.days,
    minutes: inputs.minutes,
    focus: t.form.focusOptions[inputs.focus],
    arm: t.form.armOptions[inputs.arm],
  });
  const H = headingLevel;
  return (
    <div>
      <H className={`${H === 'h2' ? 'text-2xl md:text-3xl' : 'text-xl'} font-extrabold text-ink`}>{t.plan.heading}</H>
      <p className="text-sm font-bold text-muted mt-1 mb-4">{summary}</p>

      {plan.lowEquipment && (
        <p role="status" className="rounded-2xl bg-[var(--pop-4-soft)] text-ink text-sm p-4 mb-4">{t.plan.lowEquipment}</p>
      )}
      {!plan.lowEquipment && plan.skippedPatterns.length > 0 && (
        <p className="rounded-2xl bg-elevated text-muted text-sm p-3 mb-4">
          {fmt(t.plan.skipped, { list: plan.skippedPatterns.map((p) => pl[p]).join(', ') })}
        </p>
      )}

      <div className="grid gap-3">
        {plan.days.map((d) =>
          d.session ? (
            <SessionCard key={d.weekday} session={d.session} weekday={d.weekday} level={inputs.level} lang={lang} t={t} onShuffle={onShuffle} />
          ) : (
            <RecoveryCard key={d.weekday} weekday={d.weekday} lang={lang} t={t} />
          ),
        )}
      </div>

      {showNotes && (
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div className="card-float p-5 break-inside-avoid">
            <h3 className="font-black text-ink mb-2">{t.form.arm}</h3>
            <p className="text-sm text-muted">{t.plan.armNotes[inputs.arm]}</p>
          </div>
          <div className="card-float p-5 break-inside-avoid">
            <h3 className="font-black text-ink mb-2">{t.plan.progressionHeading}</h3>
            <ul className="space-y-1.5 text-sm text-muted list-disc pl-5">
              {t.plan.progression[inputs.level].map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export function SafetyBox({ lang }: { lang: Lang }) {
  const t = workoutStrings(lang);
  return (
    <section aria-labelledby="safety-heading" className="rounded-3xl border-2 border-[var(--pop-4)] bg-[var(--pop-4-soft)] p-5 sm:p-6 break-inside-avoid">
      <h2 id="safety-heading" className="text-xl font-extrabold text-ink mb-3">⚠️ {t.safety.heading}</h2>
      <ul className="space-y-2 text-sm text-ink/90 list-disc pl-5">
        {t.safety.items.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Form + live plan
// ---------------------------------------------------------------------------

function Segmented<T extends string | number>({
  name,
  legend,
  value,
  options,
  onChange,
}: {
  name: string;
  legend: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="mb-5">
      <legend className="text-sm font-black text-ink mb-2">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const id = `${name}-${o.value}`;
          const checked = o.value === value;
          return (
            <label
              key={String(o.value)}
              htmlFor={id}
              className={`cursor-pointer rounded-full border-2 px-3.5 py-1.5 text-sm font-bold transition-colors has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-[var(--brand-soft)] ${
                checked
                  ? 'border-[var(--brand)] bg-[var(--brand)] text-white'
                  : 'border-line bg-surface text-muted hover:border-[var(--brand)] hover:text-ink'
              }`}
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={String(o.value)}
                checked={checked}
                onChange={() => onChange(o.value)}
                className="sr-only"
              />
              {o.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export function WorkoutGenerator({ lang, embed = false }: { lang: Lang; embed?: boolean }) {
  const t = workoutStrings(lang);
  const c = getStrings(lang).exercisesChrome;
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [swaps, setSwaps] = useState<Swaps>({});
  // Only rewrite the address bar once the visitor has changed something (or
  // arrived with a share link), so a plain visit keeps a clean URL.
  const [dirty, setDirty] = useState(false);

  // Read the share URL after hydration (prerendered HTML = default plan).
  useEffect(() => {
    if (window.location.search) {
      const parsed = parseInputs(new URLSearchParams(window.location.search));
      setInputs(parsed.inputs);
      setSwaps(parsed.swaps);
      setDirty(true);
    }
  }, []);

  const query = serializeInputs(inputs, swaps);
  useEffect(() => {
    if (!dirty) return;
    const url = `${window.location.pathname}?${query}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', url);
  }, [query, dirty]);

  const plan = useMemo(() => generatePlan(inputs, lang, swaps), [inputs, lang, swaps]);

  const update = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    setInputs((p) => ({ ...p, [k]: v }));
    setSwaps({}); // slot keys change with inputs
    setDirty(true);
  };
  const toggleEq = (o: EquipmentOption) =>
    update('equipment', inputs.equipment.includes(o) ? inputs.equipment.filter((x) => x !== o) : [...inputs.equipment, o]);

  const onShuffle = (key: string) => {
    const item = plan.days.flatMap((d) => (d.session ? [...d.session.main, ...d.session.accessory] : [])).find((i) => i.slotKey === key);
    if (!item) return;
    setSwaps((s) => ({ ...s, [key]: ((s[key] || 0) + 1) % item.options }));
    setDirty(true);
  };

  const shareUrl = () => `${SITE_URL}${toolPath(lang, WORKOUT_SLUG)}?${query}`;

  return (
    <div className={`grid gap-6 ${embed ? 'md:grid-cols-[300px_1fr]' : 'lg:grid-cols-[340px_1fr]'} items-start`}>
      <form
        className="card-float p-5 sm:p-6 lg:sticky lg:top-24 print:hidden"
        onSubmit={(e) => e.preventDefault()}
        aria-label={t.form.heading}
      >
        <h2 className="text-xl font-extrabold text-ink mb-4">{t.form.heading}</h2>
        <Segmented
          name="level"
          legend={t.form.level}
          value={inputs.level}
          options={LEVELS.map((l) => ({ value: l, label: c.difficulty[l] }))}
          onChange={(v) => update('level', v)}
        />
        <Segmented
          name="days"
          legend={t.form.days}
          value={inputs.days}
          options={DAY_OPTIONS.map((d) => ({ value: d, label: fmt(t.form.daysOption, { n: d }) }))}
          onChange={(v) => update('days', v)}
        />
        <Segmented
          name="minutes"
          legend={t.form.minutes}
          value={inputs.minutes}
          options={MINUTE_OPTIONS.map((m) => ({ value: m, label: fmt(t.form.minutesOption, { n: m }) }))}
          onChange={(v) => update('minutes', v)}
        />
        <Segmented
          name="focus"
          legend={t.form.focus}
          value={inputs.focus}
          options={FOCUSES.map((f) => ({ value: f, label: t.form.focusOptions[f] }))}
          onChange={(v) => update('focus', v)}
        />
        <Segmented
          name="arm"
          legend={t.form.arm}
          value={inputs.arm}
          options={ARMS.map((a) => ({ value: a, label: t.form.armOptions[a] }))}
          onChange={(v) => update('arm', v)}
        />
        <fieldset className="mb-2">
          <legend className="text-sm font-black text-ink mb-2">{t.form.equipment}</legend>
          <label className="flex items-center gap-2.5 py-1 text-sm text-faint">
            <input type="checkbox" checked disabled className="w-4 h-4 accent-primary" />
            {t.form.equipmentHome}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-x-3">
            {EQUIPMENT_OPTIONS.map((o) => (
              <label key={o} className="flex items-center gap-2.5 py-1 text-sm text-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={inputs.equipment.includes(o)}
                  onChange={() => toggleEq(o)}
                  className="w-4 h-4 accent-primary"
                />
                {equipmentOptionLabel(o, lang, t)}
              </label>
            ))}
          </div>
        </fieldset>
      </form>

      <div className="min-w-0" id="plan">
        <div className="flex flex-wrap gap-2 mb-4 print:hidden">
          <CopyButton getText={shareUrl} label={`🔗 ${t.actions.copyLink}`} copiedLabel={t.actions.copied} className="btn-3d btn-3d--sm px-4 py-2" />
          <button type="button" onClick={() => window.print()} className="btn-3d btn-3d--ghost btn-3d--sm px-4 py-2">
            🖨️ {t.actions.print}
          </button>
          {Object.keys(swaps).length > 0 && (
            <button type="button" onClick={() => setSwaps({})} className="btn-3d btn-3d--ghost btn-3d--sm px-4 py-2">
              ↺ {t.actions.resetSwaps}
            </button>
          )}
        </div>
        <PlanView plan={plan} lang={lang} onShuffle={onShuffle} />
        <div className="mt-4">
          <SafetyBox lang={lang} />
        </div>
      </div>
    </div>
  );
}
